import { EntityLegalType } from './../../../models/entity';
import { AuthFacade } from 'src/app/store/auth/facade/auth.facade';
import { IJwtoken } from 'src/app/models/jwtoken';
import { AuthService } from 'src/app/services/http/auth.service';
import { ClientHomeRouteService } from './../../../services/utils/client-home-route.service';
import { GetInvestorPortfolios } from 'src/app/store/portfolios/actions/portfolios.actions';
import { KiwiSaverRedirectService } from './../../../services/utils/kiwisaver-redirect.service';
import { ClientService } from 'src/app/services/http/client.service';
import { IRegisterKiwiSaverUser } from './../../../models/registration/register';
import * as RegistrationKiwiSaverActions from './../actions/registration-kiwisaver.actions';
import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { switchMap, catchError, map } from 'rxjs/operators';
import { RegisterService } from 'src/app/services/http/register.service';
import { of, Subscription, Observable } from 'rxjs';
import * as ErrorActions from '../../global-notification/actions/global-notification.action';
import { Router, RouterStateSnapshot } from '@angular/router';

@Injectable()
export class RegistrationKiwiSaverEffects {

    private redirectToRouteSubscription: Subscription;

    constructor(
        private actions$: Actions,
        private router: Router,
        private registerService: RegisterService,
        private clientService: ClientService,
        private kiwiSaverRedirectService: KiwiSaverRedirectService,
        private clientHomeRouteService: ClientHomeRouteService,
        private authService: AuthService,
        private authFacade: AuthFacade) { }


    getKiwiSaverEntity$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RegistrationKiwiSaverActions.REGISTER_KIWISAVER_GET_STATUS),
            switchMap((action: RegistrationKiwiSaverActions.RegistrationKiwiSaverGetStatus) => {
                return this.clientService.getKiwiSaverEntity().pipe(
                    switchMap((res: any) => {
                        const stateRes: Partial<IRegisterKiwiSaverUser> = {
                            EntityId: res.EntityId,
                            EntityName: res.EntityName,
                            LegalEntityType: EntityLegalType.Individual,
                            Status: res.Status
                        };
                        return of(
                            new RegistrationKiwiSaverActions.RegistrationKiwiSaverUpdateEntity(stateRes),
                            new RegistrationKiwiSaverActions.RegistrationKiwiSaverGetStatusSuccess()
                        );
                    }),
                    catchError((err) => {
                        const errorText = (err.error && err.error.message) ? err.error.message : "Investor not supported";
                        if (err.status === 400) {
                            // Log user out with no redirect
                            this.authFacade.logout$();
                            return of(
                                new ErrorActions.NotificationErrorAction(errorText)
                            );
                        } else {
                            return of(
                                new RegistrationKiwiSaverActions.RegistrationKiwiSaverGetStatusFail(errorText),
                                new ErrorActions.NotificationErrorAction(errorText)
                            );
                        }
                    })
                );
            })
        );
    });

    register$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RegistrationKiwiSaverActions.REGISTER_KIWISAVER),
            switchMap((action: RegistrationKiwiSaverActions.RegisterKiwiSaver) => {
                return this.registerService.registerKiwiSaver(<Partial<IRegisterKiwiSaverUser>>action.payload).pipe(
                    switchMap((res: any) => {
                        // first we need to refresh the token, otherwise we won't be able to retrieve the newly created portfolio
                        return this.authService.refreshToken().pipe(
                            switchMap((result: IJwtoken) => {
                                if (result) {
                                    const stateRes = {
                                        EntityId: res.EntityId,
                                        EntityName: res.EntityName,
                                        LegalEntityType: EntityLegalType.Individual,
                                        Status: res.Status
                                    };
                                    return of(
                                        new RegistrationKiwiSaverActions.RegisterKiwiSaverSuccess,
                                        new GetInvestorPortfolios(),
                                        new RegistrationKiwiSaverActions.RegistrationKiwiSaverUpdateEntity(stateRes),
                                        new RegistrationKiwiSaverActions.RegisterKiwiSaverRedirect
                                    );
                                }
                            }));
                    }),
                    catchError((err) => {
                        const errorText = (err.error && err.error.message) ? err.error.message : "An error has occured while registering";
                        return of(
                            new ErrorActions.NotificationErrorAction(errorText),
                            new RegistrationKiwiSaverActions.RegisterKiwiSaverFail(errorText)
                        );
                    })
                );
            })
        );
    });

    registrationKiwisaverRedirect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RegistrationKiwiSaverActions.REGISTER_KIWISAVER_REDIRECT),
            map((action: RegistrationKiwiSaverActions.RegisterKiwiSaverRedirect) => {
                if (this.redirectToRouteSubscription) {
                    this.redirectToRouteSubscription.unsubscribe();
                }
                this.redirectToRouteSubscription = this.kiwiSaverRedirectService.getRedirectRoute$().subscribe((route: string) => {
                    if (route === '*') {
                        this.clientHomeRouteService.redirectToHome();
                    } else {
                        const snapshot: RouterStateSnapshot = this.router.routerState.snapshot;
                        if (snapshot.url !== route) {
                            this.router.navigateByUrl(route);
                        }
                    }
                });
            })
        );
    }, { dispatch: false });

}
